import * as React from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { SequenceGrid, UnitClickHandler } from "@loschmidt/commons";
import Tippy from "@tippyjs/react";
import MutationPopoverSelect from "./MutationPopoverSelect";
import { useCallback, useMemo, useState } from "react";
import { useInputContext } from "../../context/input-context";
import { MutationLevelEnum } from "../../types/mutation";

// by "unit" we mean amino-acid or nucleotide
// each unit is represented by a string with length 1

// This component currently allows only one mutated position.

function MutationModal({close}: {close: () => void}) {

  // Context:

  const {
    targetSelected,
    sequence,
    setMutations,
  } = useInputContext();
  
  // State:
  
  const [activePosition, setActivePosition] = useState<number | null>(null);
  const [mutatedPosition, setMutatedPosition] = useState<number | null>(null);
  const [targetUnits, setTargetUnits] = useState<string[] | null>(null);
  const [selectedUnitElement, setSelectedUnitElement] = useState<HTMLSpanElement | null>(null);

  const dirty = activePosition !== null || mutatedPosition !== null;

  // Callbacks:
  
  const handleBackdropClick = useCallback(() => {
    if (!dirty) {
      close();
    }
  }, [dirty, close]);

  const selectUnitsForPositionLocally = useCallback((units: string[], position: number) => {
    setMutatedPosition(position);
    setTargetUnits(units);
  }, []);

  const toggleActivePosition = useCallback((position: number) => {
    setActivePosition(activePosition => activePosition === position ? null : position);
  }, []);

  const handleUnitClick: UnitClickHandler<string> = useCallback(({target, position}) => {
    toggleActivePosition(position);
    setSelectedUnitElement(curr => curr === target ? null : target);
  }, [toggleActivePosition]);

  const handleTippyClickOutside = useCallback((tippy) => {
    if (tippy.state.isMounted) {
      setSelectedUnitElement(null);
    }
  }, []);

  const handleSubmit = useCallback(() => {
    if (mutatedPosition !== null && targetUnits !== null) {
      setMutations([{units: targetUnits, position: mutatedPosition}])
    }
    close();
  }, [mutatedPosition, targetUnits, close, setMutations]);

  // Predicates for sequence grid:

  const essentialPredicate = useMemo(() => {
    if (!targetSelected) return undefined;
    return ({position}: {position: number}) => targetSelected.essentialResiduePositions.includes(position);
  }, [targetSelected]);

  const nonStructuralPredicate = useMemo(() => {
    if (!targetSelected || !targetSelected.structResidues) return undefined;
    return ({position}: {position: number}) => !targetSelected.structResidues!.includes(position);
  }, [targetSelected]);

  const activePredicate = useCallback(({position}: {position: number}) => {
    return position === activePosition;
  }, [activePosition]);

  const selectedPredicate = useCallback(({position}: {position: number}) => {
    return position === mutatedPosition;
  }, [mutatedPosition]);

  // Memos:

  const popoverSelectedUnits = useMemo(() => {
    return typeof activePosition === 'number' && activePosition === mutatedPosition && targetUnits ? targetUnits : [];
  }, [mutatedPosition, activePosition, targetUnits]);

  // Render section:

  return (
    <Modal isOpen={true} centered={true} fade={false} size='xl' toggle={handleBackdropClick} className="ll-mutation-table-modal" style={{ width: '950px' }}>
      <ModalHeader toggle={close}>
        Select mutation using sequence table
      </ModalHeader>
      <ModalBody>
        <SequenceGrid
          sequence={sequence}
          essential={essentialPredicate}
          nonStructural={nonStructuralPredicate}
          active={activePredicate}
          selected={selectedPredicate}
          onClick={handleUnitClick}
        >
          {typeof activePosition === 'number' && selectedUnitElement !== null && (
            <Tippy
              reference={selectedUnitElement}
              content={(
                <MutationPopoverSelect
                  level={MutationLevelEnum.Protein}
                  selected={popoverSelectedUnits}
                  disabled={sequence[activePosition - 1]}
                  selectUnitsForPosition={selectUnitsForPositionLocally}
                  position={activePosition}
                />
              )}
              showOnCreate={true}
              placement="right"
              trigger="click"
              theme="light-border"
              interactive={true}
              onClickOutside={handleTippyClickOutside}
              hideOnClick={false}
              offset={[0, 8]}
              duration={0}
              appendTo={document.body}
            />
          )}
        </SequenceGrid>
      </ModalBody>
      <ModalFooter className="ll-sequence-grid">
        <div title="Essential residues in the sequence are indicated in red.">
          <span className="ll-sequence-grid-residue ll-essential">X</span> - essential residue
        </div>
        {targetSelected?.structResidues && (
          <div title="Residues without structural information have a grey background.">
            <span className="ll-sequence-grid-residue ll-nonstructural">X</span> - no structural information
          </div>
        )}
        <div style={{ flex: 1 }} />
        <Button color="primary" onClick={handleSubmit}>Submit</Button>
      </ModalFooter>
    </Modal>
  );
}

function MutationTableModalButton() {
  const [open, setOpen] = useState(false);
  const toggleOpen = useCallback(() => setOpen(open => !open), []);
  return <div>
    <Button onClick={toggleOpen}>Sequence input</Button>
    {open && <MutationModal close={toggleOpen} />}
  </div>
}

export default MutationTableModalButton;
